import {api} from "@/shared/api";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import * as Input from "@/shared/utils/input";
import {ResetTrialDataResponse} from "@/shared/timr";

const template = `
	<form data-trial-data-reset-form="">
		<div class="mb-20">
			<label class="checkbox-inline ml-0" for="workingTimeAndProjectTimesCheckbox">
				<input type="checkbox" id="workingTimeAndProjectTimesCheckbox" name="resetWorkingTimeAndProjectTimes" data-available-if="hasWorkingTimeOrProjectTimes">
				${messages["resetTrialData.workingTimeAndProjectsTime"]}
			</label>
			<div class="ml-30 display-grid" data-relation-mapping="hasWorkingTimeOrProjectTimes" data-header="hasWorkingTimeOrProjectTimes">
				<label class="checkbox-inline ml-0 mt-8">
					<input type="checkbox" disabled data-prevent-toggle="">
					${messages["resetTrialData.workingTimes"]}
				</label>
				<label class="checkbox-inline ml-0 mt-8">
					<input type="checkbox" disabled data-prevent-toggle="">
					${messages["resetTrialData.workingTimeRequests"]}
				</label>
				<label class="checkbox-inline ml-0 mt-8" >
					<input type="checkbox" disabled data-prevent-toggle="">
					${messages["resetTrialData.projectTimes"]}
				</label>
				<label class="checkbox-inline ml-0 mt-8" for="workingScheduleModelsCheckbox">
					<input type="checkbox" id="workingScheduleModelsCheckbox" name="resetWorkScheduleModels" disabled data-available-if="hasWorkScheduleModels">
					${messages["resetTrialData.workScheduleModels"]}
				</label>
				<label class="checkbox-inline ml-0 mt-8" for="breakModelsCheckbox">
					<input type="checkbox" id="breakModelsCheckbox" name="resetBreakModels" disabled data-available-if="hasBreakModels">
					${messages["resetTrialData.breakModels"]}
				</label>
				<label class="checkbox-inline ml-0 mt-8" for="tasksCheckbox">
					<input type="checkbox" id="tasksCheckbox" name="resetTasks" disabled data-available-if="hasTasks">
					${messages["resetTrialData.tasks"]}
				</label>
			</div>
		</div>
		<div data-header="hasDriveLogs">${messages["resetTrialData.driveLog.title"]}</div>
		<div class="mb-20 mt-5">
			<label class="checkbox-inline ml-0" for="driveLogsCheckbox">
				<input type="checkbox" id="driveLogsCheckbox" name="resetDriveLogs" data-available-if="hasDriveLogs">
				${messages["resetTrialData.driveLogs"]}
			</label>
			<div class="ml-30 display-grid" data-relation-mapping="hasDriveLogs">
				<label class="checkbox-inline ml-0 mt-8" for="driveLogCategoriesCheckbox">
					<input type="checkbox" id="driveLogCategoriesCheckbox" name="resetDriveLogCategories" disabled data-available-if="hasDriveLogCategories">
					${messages["resetTrialData.driveLogCategories"]}
				</label>
				<label class="checkbox-inline ml-0 mt-8" for="carsCheckbox">
					<input type="checkbox" id="carsCheckbox" name="resetCars" disabled data-available-if="hasCars">
					${messages["resetTrialData.cars"]}
				</label>
			</div>
		</div>
		<div data-header="hasGroups">${messages["resetTrialData.general"]}</div>
		<div class="mt-5">
			<label class="checkbox-inline ml-0" for="groupsCheckbox">
				<input type="checkbox" id="groupsCheckbox" name="resetGroups" data-available-if="hasGroups">
				${messages["resetTrialData.groups"]}
			</label>
		</div>
	</form>
	<div style="display: none" data-no-available-data="">${messages["resetTrialData.noAvailableData"]}</div>
	`;


export class ResetTrialDataDialog extends HTMLElement {

	private availableData: ResetTrialDataResponse;

	public static show(): JQueryPromise<void> {
		const dialog: ResetTrialDataDialog = document.createElement('reset-trial-data-dialog') as ResetTrialDataDialog;
		return dialog.loadAvailableData().done(() => dialog.open());
	}

	private connectedCallback(): void {
		this.innerHTML = template;
		this.initStaticContent();
		this.initRelations();
	}

	private initStaticContent(): void {
		if (this.hasAvailableDataToDeleteFrom()) {
			this.querySelectorAll<HTMLInputElement>("input[type=checkbox]:not([data-prevent-toggle])").forEach(checkbox => {
				let isAvailable: boolean = this.availableData[checkbox.dataset.availableIf as keyof ResetTrialDataResponse] === true;
				if (!isAvailable) {
					const relations = this.querySelectorAll<HTMLElement>(`[data-relation-mapping="${checkbox.dataset.availableIf}"] input:not([data-prevent-toggle])`);
					isAvailable = Array.from(relations).some(relation => {
						return this.availableData[relation.dataset.availableIf as keyof ResetTrialDataResponse] === true;
					});
				}
				checkbox.closest("label").style.display = isAvailable ? "block" : "none";
				this.querySelectorAll<HTMLElement>(`[data-header="${checkbox.dataset.availableIf}"]`).forEach(header => {
					header.style.display = isAvailable ? "grid" : "none";
				});
			});
		} else {
			const noDataAvailableElement: HTMLElement = this.querySelector("[data-no-available-data]");
			noDataAvailableElement.style.display = "block";
			const trialDataResetForm: HTMLElement = this.querySelector("[data-trial-data-reset-form]");
			trialDataResetForm.style.display = "none";
			// document because the button is not a child of this element
			Input.disable(document.querySelector(".applyButton"));
		}
	}

	private initRelations() {
		this.querySelectorAll<HTMLInputElement>("input[type=checkbox]:not([data-prevent-toggle])").forEach(checkbox => {
			checkbox.addEventListener("change", e => {
				const target = e.target as HTMLInputElement;
				this.querySelectorAll<HTMLInputElement>(`[data-relation-mapping="${target.dataset.availableIf}"] input`)
					.forEach(input => {
						if (input.dataset.preventToggle !== undefined) {
							// set checkbox state to prevent misunderstandings
							input.checked = !target.checked;
						} else {
							Input.toggleCheckbox(input, target.checked, true);
						}
					});
			});
		});
	}

	private hasAvailableDataToDeleteFrom(): boolean {
		// Object.values not supported in ES2025 (Added ES2017)
		return Object.keys(this.availableData).map(e => this.availableData[e as keyof ResetTrialDataResponse]).some(Boolean);
	}

	private open(): void {
		new BootstrapModal.Modal({
			title: "resetTrialData.title",
			message: $(this),
			destroyOnClose: true,
			buttons: [
				{
					text: "dialog.cancel",
					css: "btn-default pull-left"
				},
				{
					text: "dialog.delete",
					css: "applyButton btn-danger",
					action: (m) => {
						this.confirmDeletion(m);
					}
				}
			],
		}).show();
	}

	private loadAvailableData(): JQueryPromise<void> {
		return api("account/getTrialDataResetData.json")
			.get()
			.then((response: ResetTrialDataResponse) => {
				this.availableData = response;
			});
	}

	private confirmDeletion(resetModal: BootstrapModal.Modal): void {
		BootstrapModal.confirm({
			title: "dialog.warning.title",
			message: `<form class="form-horizontal col-xs-12">
								<div>${messages["dialog.confirm.delete.trialData"]}</div>
								<div class="form-group mt-5">
									<label class="col-lg-12" for="confirmDeleteTextInput">
										${messages["dialog.confirm.delete.trialData.info"]}
									</label>
									<div class="col-lg-12">
										<input type="text" id="confirmDeleteTextInput" class="form-control" />
									</div>
								</div>
							</form>`,
			destroyOnClose: false,
			escapeText: false,
			yesLabel: "confirmation.delete",
			yesCss: "btn-danger",
			yesAction: (modal, button) => {
				const confirmDeleteTextInput = modal.find("#confirmDeleteTextInput");
				Input.removeFeedback(confirmDeleteTextInput);
				if (confirmDeleteTextInput.val() === messages["dialog.confirm.delete.trialData.comparison"]) {
					void api("admin/resetTrialData.json")
						.postForm(this.querySelector("[data-trial-data-reset-form]"))
						.always(() => {
							Button.resetLoading(button);
							modal.close();
							resetModal.close();
						});
				} else {
					Input.addFeedback(confirmDeleteTextInput, "has-error", messages["dialog.confirm.delete.trialData.error"]);
					setTimeout(() => Button.resetLoading(button), 200);
				}
			},
			noLabel: "confirmation.cancel"
		});
	}
}
